import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FocalImage } from "@/components/ui/focal-image";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ImageCropDialog } from "@/components/recipes/ImageCropDialog";
import type { RecipeBox } from "@/lib/types";
import { Crop } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { RecipeBoxFormValues } from "../_lib/recipeBoxForm";

export type EditRecipeBoxDialogProps = Readonly<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<RecipeBoxFormValues>;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onFileChange: (file: File | null) => void;
  recipeBox: RecipeBox | null;
  onCancel: () => void;
  selectedFileName?: string;
  onCropSave?: (focalPointX: number, focalPointY: number, zoom: number) => void;
}>;

export const EditRecipeBoxDialog = ({
  isOpen,
  onOpenChange,
  form,
  isSubmitting,
  errorMessage,
  onSubmit,
  onFileChange,
  recipeBox,
  onCancel,
  selectedFileName,
  onCropSave,
}: EditRecipeBoxDialogProps) => {
  const [cropDialogOpen, setCropDialogOpen] = useState(false);

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-headline">Edit Recipe Box</DialogTitle>
          <DialogDescription>Update the title, description, or cover image.</DialogDescription>
        </DialogHeader>
        {recipeBox ? (
          <div className="grid gap-4">
            {recipeBox.imageUrl ? (
              <div className="space-y-2">
                <div className="relative h-40 w-full overflow-hidden rounded-md">
                  <FocalImage
                    src={recipeBox.imageUrl}
                    alt={recipeBox.title}
                    className="h-full w-full"
                    focalPointX={recipeBox.imageFocalPointX}
                    focalPointY={recipeBox.imageFocalPointY}
                    zoom={recipeBox.imageZoom}
                  />
                </div>
                {onCropSave && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCropDialogOpen(true)}
                    disabled={isSubmitting}
                    className="w-full"
                  >
                    <Crop className="mr-2 h-4 w-4" />
                    Adjust Position
                  </Button>
                )}
              </div>
            ) : null}
            <Form {...form}>
              <form onSubmit={onSubmit} className="grid gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Sunday Suppers" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea placeholder="What makes this box special?" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-2">
                  <FormLabel htmlFor="edit-cover">Replace Cover Image</FormLabel>
                  <Input
                    id="edit-cover"
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      onFileChange(file);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Uploading a new file will replace the current cover image.
                    {selectedFileName ? ` Selected: ${selectedFileName}` : ""}
                  </p>
                </div>

                {errorMessage ? (
                  <p className="text-sm text-destructive" role="alert">
                    {errorMessage}
                  </p>
                ) : null}

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    {recipeBox?.imageUrl && onCropSave && (
      <ImageCropDialog
        open={cropDialogOpen}
        onOpenChange={setCropDialogOpen}
        imageUrl={recipeBox.imageUrl}
        initialFocalPointX={recipeBox.imageFocalPointX}
        initialFocalPointY={recipeBox.imageFocalPointY}
        initialZoom={recipeBox.imageZoom}
        onSave={onCropSave}
      />
    )}
    </>
  );
};

